import { AlertCircle, Inbox, RefreshCw, type LucideIcon } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import type { DashboardProjectionStatus } from "@/features/dashboard/types";
import { useDelayedFlag } from "@/hooks/useDelayedFlag";

export interface DashboardRequestState {
  hasData: boolean;
  isPending?: boolean;
  isFetching?: boolean;
  error?: unknown;
}

interface DashboardStateProps {
  analytics: DashboardRequestState;
  operations: DashboardRequestState;
  analyticsProjectionStatus?: DashboardProjectionStatus;
  operationsProjectionStatus?: DashboardProjectionStatus;
  isEmpty?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  onRetryAnalytics?: () => void;
  onRetryOperations?: () => void;
  loadingDelayMs?: number;
  refreshDelayMs?: number;
  skeleton?: ReactNode;
  operationsContent?: ReactNode;
  operationsSkeleton?: ReactNode;
  children: ReactNode;
}

const STATE_CSS = `
  @keyframes dashboard-state-pulse {
    0%, 100% { opacity: 0.42; }
    50% { opacity: 0.72; }
  }

  @keyframes dashboard-refresh-pass {
    from { opacity: 0.35; transform: translateX(-100%) scaleX(0.45); }
    to { opacity: 0.9; transform: translateX(250%) scaleX(0.65); }
  }

  .dashboard-state-skeleton[data-visible="true"] .dashboard-state-skeleton-block {
    animation: dashboard-state-pulse 1.5s ease-in-out infinite;
  }

  .dashboard-refresh-indicator::after {
    content: "";
    position: absolute;
    inset: 0;
    width: 40%;
    transform-origin: left center;
    background: var(--login-accent);
    animation: dashboard-refresh-pass 1.1s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .dashboard-state-skeleton-block,
    .dashboard-refresh-indicator::after {
      animation: none !important;
    }

  }
`;

const panelStyle: CSSProperties = {
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  border: "1px solid var(--login-border)",
  borderRadius: "0.75rem",
  background: "var(--login-surface)",
  color: "var(--login-text)",
};

interface StatePanelProps {
  testId: string;
  role: "alert" | "status";
  icon: LucideIcon;
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
  tone?: "neutral" | "warning" | "error";
}

function StatePanel({
  testId,
  role,
  icon: Icon,
  title,
  description,
  actionLabel,
  onAction,
  tone = "neutral",
}: StatePanelProps) {
  const toneColor =
    tone === "error"
      ? "var(--dashboard-semantic-negative, #b42318)"
      : tone === "warning"
        ? "var(--dashboard-semantic-warning, #a15c00)"
        : "var(--login-accent)";

  return (
    <div
      data-testid={testId}
      role={role}
      style={{
        ...panelStyle,
        display: "grid",
        gridTemplateColumns: "auto minmax(0, 1fr)",
        alignItems: "center",
        gap: "0.75rem",
        padding: "clamp(0.875rem, 2.4vw, 1.125rem)",
        borderLeft: `3px solid ${toneColor}`,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: "2.25rem",
          height: "2.25rem",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: toneColor,
          borderRadius: "0.5rem",
          background: `color-mix(in srgb, ${toneColor} 10%, transparent)`,
        }}
      >
        <Icon size={19} strokeWidth={2} />
      </span>
      <div style={{ minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            color: "var(--login-text)",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "clamp(0.94rem, 2.2vw, 1.02rem)",
            fontWeight: 700,
            lineHeight: 1.25,
          }}
        >
          {title}
        </p>
        <p
          style={{
            margin: "0.2rem 0 0",
            color: "var(--dashboard-body-secondary)",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "clamp(0.82rem, 2vw, 0.9rem)",
            lineHeight: 1.4,
          }}
        >
          {description}
        </p>
        {actionLabel && onAction ? (
          <button
            type="button"
            onClick={onAction}
            style={{
              minHeight: "44px",
              marginTop: "0.55rem",
              padding: "0 0.875rem",
              border: "1px solid var(--login-border)",
              borderRadius: "0.5rem",
              background: "var(--login-bg)",
              color: "var(--login-text)",
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "0.84rem",
              fontWeight: 700,
            }}
          >
            {actionLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function DashboardStateSkeleton() {
  return (
    <div
      aria-hidden="true"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: "0.75rem",
        minWidth: 0,
      }}
    >
      {["kpi-1", "kpi-2", "kpi-3", "kpi-4", "analysis"].map((block, index) => (
        <div
          key={block}
          data-testid="dashboard-skeleton-block"
          className="dashboard-state-skeleton-block"
          style={{
            minWidth: 0,
            minHeight: index === 4 ? "15rem" : "8.5rem",
            gridColumn: index === 4 ? "1 / -1" : undefined,
            border: "1px solid var(--login-border)",
            borderRadius: "0.75rem",
            background:
              "color-mix(in srgb, var(--login-border) 52%, var(--login-surface))",
            opacity: 0.55,
          }}
        />
      ))}
    </div>
  );
}

function DashboardRefreshIndicator() {
  return (
    <span
      data-testid="dashboard-refresh-indicator"
      role="status"
      aria-label="Atualizando indicadores"
      className="dashboard-refresh-indicator"
      style={{
        position: "absolute",
        zIndex: 1,
        inset: "0 0 auto",
        height: "2px",
        overflow: "hidden",
        pointerEvents: "none",
      }}
    ></span>
  );
}

interface DashboardOperationsRegionProps {
  operations: DashboardRequestState;
  projectionStatus: DashboardProjectionStatus;
  content?: ReactNode;
  skeleton?: ReactNode;
  skeletonVisible: boolean;
  onRetry?: () => void;
}

function DashboardOperationsRegion({
  operations,
  projectionStatus,
  content,
  skeleton,
  skeletonVisible,
  onRetry,
}: DashboardOperationsRegionProps) {
  if (projectionStatus === "not_ready") {
    return (
      <StatePanel
        testId="dashboard-operations-not-ready"
        role="status"
        icon={RefreshCw}
        title="Operações atuais em preparação"
        description="Os dados operacionais ainda estão sendo conferidos."
        actionLabel={onRetry ? "Verificar operações" : undefined}
        onAction={onRetry}
      />
    );
  }

  if (!operations.hasData && !operations.error) {
    if (!operations.isPending && !operations.isFetching) return null;
    return (
      <div
        data-testid="dashboard-operations-loading"
        role="status"
        aria-busy="true"
        aria-label="Carregando operações atuais"
        data-skeleton-visible={skeletonVisible ? "true" : "false"}
        data-visible={skeletonVisible ? "true" : "false"}
        className="dashboard-state-skeleton"
        style={{
          minWidth: 0,
          minHeight: "8.5rem",
          opacity: skeletonVisible ? 1 : 0,
        }}
      >
        {skeleton ?? (
          <div
            aria-hidden="true"
            data-testid="dashboard-skeleton-block"
            className="dashboard-state-skeleton-block"
            style={{
              minHeight: "8.5rem",
              border: "1px solid var(--login-border)",
              borderRadius: "0.75rem",
              background:
                "color-mix(in srgb, var(--login-border) 52%, var(--login-surface))",
              opacity: 0.55,
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div
      data-testid="dashboard-operations-region"
      aria-busy={Boolean(operations.isFetching)}
      style={{ display: "grid", gap: "0.75rem", minWidth: 0 }}
    >
      {projectionStatus === "stale" ? (
        <StatePanel
          testId="dashboard-operations-stale"
          role="status"
          icon={RefreshCw}
          title="Operações podem estar desatualizadas"
          description="Mostrando o último resultado operacional disponível."
          tone="warning"
        />
      ) : null}
      {operations.error ? (
        <StatePanel
          testId="dashboard-operations-error"
          role="alert"
          icon={AlertCircle}
          title={
            operations.hasData
              ? "Não foi possível atualizar as operações atuais"
              : "Não foi possível carregar as operações atuais"
          }
          description={
            operations.hasData
              ? "O último resultado operacional válido continua visível."
              : "Os indicadores do período continuam disponíveis."
          }
          actionLabel={onRetry ? "Tentar operações novamente" : undefined}
          onAction={onRetry}
          tone="warning"
        />
      ) : null}
      {operations.hasData && content ? (
        <div data-testid="dashboard-operations-content" style={{ minWidth: 0 }}>
          {content}
        </div>
      ) : null}
    </div>
  );
}

export function DashboardState({
  analytics,
  operations,
  analyticsProjectionStatus = "fresh",
  operationsProjectionStatus = "fresh",
  isEmpty = false,
  emptyTitle = "Nenhum dado neste período",
  emptyDescription = "Escolha outro período para consultar o histórico.",
  onRetryAnalytics,
  onRetryOperations,
  loadingDelayMs = 160,
  refreshDelayMs = 200,
  skeleton,
  operationsContent,
  operationsSkeleton,
  children,
}: DashboardStateProps) {
  const initialLoading = !analytics.hasData && !analytics.error;
  const refreshing = analytics.hasData && Boolean(analytics.isFetching);
  const showSkeleton = useDelayedFlag(initialLoading, loadingDelayMs);
  const showRefreshIndicator = useDelayedFlag(refreshing, refreshDelayMs);
  const showOperationsSkeleton = useDelayedFlag(
    !operations.hasData && !operations.error,
    loadingDelayMs,
  );
  const bothRequestsFailed =
    Boolean(analytics.error) &&
    !analytics.hasData &&
    Boolean(operations.error) &&
    !operations.hasData;
  const retryDashboard =
    onRetryAnalytics || onRetryOperations
      ? () => {
          onRetryAnalytics?.();
          onRetryOperations?.();
        }
      : undefined;
  const operationsRegion = (
    <DashboardOperationsRegion
      operations={operations}
      projectionStatus={operationsProjectionStatus}
      content={operationsContent}
      skeleton={operationsSkeleton}
      skeletonVisible={showOperationsSkeleton}
      onRetry={onRetryOperations}
    />
  );

  if (analyticsProjectionStatus === "not_ready") {
    return (
      <div style={{ display: "grid", gap: "0.75rem", minWidth: 0 }}>
        <style>{STATE_CSS}</style>
        <StatePanel
          testId="dashboard-not-ready"
          role="status"
          icon={RefreshCw}
          title="Indicadores em preparação"
          description="Os dados históricos ainda estão sendo conferidos. Tente novamente em instantes."
          actionLabel={onRetryAnalytics ? "Verificar novamente" : undefined}
          onAction={onRetryAnalytics}
        />
        {operationsRegion}
      </div>
    );
  }

  if (analytics.error && !analytics.hasData) {
    if (bothRequestsFailed) {
      return (
        <div style={{ display: "grid", gap: "0.75rem", minWidth: 0 }}>
          <style>{STATE_CSS}</style>
          <StatePanel
            testId="dashboard-fatal-error"
            role="alert"
            icon={AlertCircle}
            title="Não foi possível carregar o painel"
            description="Tente novamente."
            actionLabel={retryDashboard ? "Tentar novamente" : undefined}
            onAction={retryDashboard}
            tone="error"
          />
        </div>
      );
    }

    return (
      <div style={{ display: "grid", gap: "0.75rem", minWidth: 0 }}>
        <style>{STATE_CSS}</style>
        <StatePanel
          testId="dashboard-fatal-error"
          role="alert"
          icon={AlertCircle}
          title="Não foi possível carregar os indicadores"
          description="Tente novamente."
          actionLabel={
            onRetryAnalytics ? "Tentar carregar novamente" : undefined
          }
          onAction={onRetryAnalytics}
          tone="error"
        />
      </div>
    );
  }

  if (initialLoading) {
    return (
      <div
        style={{
          display: "grid",
          gap: "0.75rem",
          minWidth: 0,
        }}
      >
        <style>{STATE_CSS}</style>
        <div
          data-testid="dashboard-initial-loading"
          aria-busy="true"
          aria-label="Carregando indicadores"
          data-skeleton-visible={showSkeleton ? "true" : "false"}
          data-visible={showSkeleton ? "true" : "false"}
          className="dashboard-state-skeleton"
          style={{
            minWidth: 0,
            minHeight: "24.25rem",
            opacity: showSkeleton ? 1 : 0,
          }}
        >
          {skeleton ?? <DashboardStateSkeleton />}
        </div>
        {operationsRegion}
      </div>
    );
  }

  const notices = (
    <>
      {analyticsProjectionStatus === "stale" ? (
        <StatePanel
          testId="dashboard-stale-warning"
          role="status"
          icon={RefreshCw}
          title="Indicadores podem estar desatualizados"
          description="Mostrando o último resultado disponível enquanto os dados são atualizados."
          tone="warning"
        />
      ) : null}
      {analytics.error && analytics.hasData ? (
        <StatePanel
          testId="dashboard-analytics-refresh-error"
          role="alert"
          icon={AlertCircle}
          title="Não foi possível atualizar os indicadores"
          description="O último resultado válido continua visível."
          actionLabel={onRetryAnalytics ? "Tentar novamente" : undefined}
          onAction={onRetryAnalytics}
          tone="warning"
        />
      ) : null}
    </>
  );

  if (isEmpty) {
    return (
      <div style={{ display: "grid", gap: "0.75rem", minWidth: 0 }}>
        <style>{STATE_CSS}</style>
        {notices}
        <StatePanel
          testId="dashboard-empty-state"
          role="status"
          icon={Inbox}
          title={emptyTitle}
          description={emptyDescription}
        />
        {operationsRegion}
      </div>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        display: "grid",
        gap: "0.75rem",
        minWidth: 0,
      }}
    >
      <style>{STATE_CSS}</style>
      {showRefreshIndicator ? <DashboardRefreshIndicator /> : null}
      {notices}
      <div
        data-testid="dashboard-retained-content"
        data-refreshing={refreshing ? "true" : "false"}
        aria-busy={refreshing}
        className="dashboard-retained-content"
        style={{
          minWidth: 0,
          opacity: 1,
        }}
      >
        {children}
      </div>
      {operationsRegion}
    </div>
  );
}
